import { Component, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService } from './services/auth.service';
import { SessionGuardService } from './services/session-guard.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LoadingService } from './loading.service';
import { environment } from '../environments/environment';
import { getZavoBridge } from './services/mcp/zavo-bridge';
import { CredentialRevealComponent } from './common-components/credential-reveal/credential-reveal.component';
import { DeveloperModeStripComponent } from './common-components/developer-mode-strip/developer-mode-strip.component';
import { AppStatusComponent } from './common-components/app-status/app-status.component';
import { ChangesPageComponent } from './common-components/changes-page/changes-page.component';
import { AppUpdateService } from './services/app-update.service';
import { WaitingChangesSender } from './services/offline/waiting-changes-sender.service';
import { MobileAccessGateComponent } from './common-components/mobile-access-gate/mobile-access-gate.component';
import { LAST_URL_KEY, PROJECT_KEY, StudioNavService } from './studio-nav/studio-nav.service';
import { StudioBarComponent } from './studio-nav/bar/studio-bar.component';
import { ImageConfirmComponent } from './common-components/image-confirm/image-confirm.component';
import { McpWiringService } from './services/mcp/mcp-wiring.service';
import { ShowHighlightOverlayComponent } from './services/mcp/tools/show-highlight-overlay.component';
import { ApprovalLayerComponent } from './shared/approval-layer/approval-layer.component';


@Component({
    selector: 'app-root',
    imports: [RouterOutlet, CommonModule, FormsModule, CredentialRevealComponent, DeveloperModeStripComponent, AppStatusComponent, ChangesPageComponent, MobileAccessGateComponent, StudioBarComponent, ImageConfirmComponent, ShowHighlightOverlayComponent, ApprovalLayerComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.css'
})
export class AppComponent {
  firstRenderFlag = true;
  title = 'olympus-editor';
  readonly isRuntime = environment.isRuntime;
  readonly isDesktop = !!getZavoBridge();
  private readonly waitingSender = inject(WaitingChangesSender);
  private readonly mcpWiring = inject(McpWiringService);
  constructor(private authService: AuthService, private router: Router, public loading: LoadingService, private sessionGuard: SessionGuardService, private appUpdate: AppUpdateService, private nav: StudioNavService) {
    if (!environment.isRuntime) this.mcpWiring.wire();
    // Plan 7B: soft updates, the offline banner's signals, the 7-day sign-in clock. Runtime only; a no-op in the editor.
    this.appUpdate.start();
    // Sends the Waiting changes kept while offline, once Zavo can be reached. Only in an app that Works offline.
    this.waitingSender.start();
    // Nav 7b (spec Q8, R4) — remembers the last screen (editor only) so a reload can return to it.
    if (!environment.isRuntime) {
      this.router.events.subscribe((e) => {
        if (e instanceof NavigationEnd && e.urlAfterRedirects.startsWith('/') && !/^\/(\?|$)|resetPassword|team-only|accept-invite|blank/.test(e.urlAfterRedirects)) {
          sessionStorage.setItem(LAST_URL_KEY, e.urlAfterRedirects);
        }
      });
    }
    console.log('Is runtime environment:', environment.isRuntime);
    // Single active session per builder (anti-account-sharing for unlimited AI). Editor only —
    // runtime end-users aren't billed for AI and use a different pool.
    if (!environment.isRuntime) this.sessionGuard.init();
    this.authService.isAuthenticated().subscribe((flag) => {
      if (flag) {
        // Never log the sign-in token: it is a bearer credential for up to an hour.
        console.log('User is authenticated.');
        if(this.firstRenderFlag) {
          this.firstRenderFlag = false;
          const currentUrl = this.router.url;
          if (!environment.isRuntime &&
              !currentUrl.includes('resetPassword') &&
              !currentUrl.includes('settings/integrations')) {
            // Was (2026-10-05, nav 7b, Q8 R4): this.router.navigate(['/dashboard/client-list']);
            // Nav 7b (spec Q8, R4) — a reload stays where it was: the tab remembers its project and last screen.
            const saved = sessionStorage.getItem(PROJECT_KEY);
            void (async () => {
              if (saved && await this.nav.openProject(saved)) {
                const last = sessionStorage.getItem(LAST_URL_KEY) || '/dashboard/entity-list';
                const needsPage = ['/ui-editor', '/dashboard/function-editor', '/dashboard/pageData'].some((p) => last.startsWith(p));
                // Nav 7b final review (I1) — the reload already drew this URL before the project was open; going
                // through /blank rebuilds the screen with it (navigating to the same URL would do nothing).
                await this.router.navigateByUrl('/blank', { skipLocationChange: true });
                this.router.navigateByUrl(needsPage ? '/dashboard/pages' : last);
              } else {
                this.router.navigate(['/dashboard/client-list']);
              }
            })();
          }
        }
      } else {
        // Unauthenticated → return to login IMMEDIATELY. This previously waited for the NEXT router
        // event (router.events.pipe(take(1))) before navigating; if no navigation was already in
        // flight when this fired (e.g. a builder clicking Sign out), the redirect stalled until the
        // user's next action — which is why logging out appeared to need a second click. Read the
        // current URL directly and navigate now; keep the resetPassword and team-only exceptions.
        // accept-invite too: an invitee is ALWAYS signed out (they have no account until they accept),
        // so without it the emailed Accept invite link flashed the page and bounced to /landing.
        if (!this.router.url.includes('resetPassword') && !this.router.url.includes('team-only') &&
            !this.router.url.includes('accept-invite')) {
          this.router.navigate(['']);
        }
      }
    });
  }
}
